import { useState, useEffect, ChangeEventHandler } from 'react';
import { DayPicker, ChevronProps } from 'react-day-picker';
import 'react-day-picker/style.css';
import s from './styles.scss';
import { ClickAwayListener, enums, utils as u, Button, Icon } from 'edrolo-ui';
import { Popover, usePopover } from '../Popover';
import {
  format,
  setHours,
  setMinutes,
  parseISO,
  isToday,
  isBefore,
} from 'date-fns';
import cx from 'classnames';
import { TimeHHMM } from './types';

interface Props {
  onChange: (date: Date | undefined) => void;
  placeholder: string;
  placement?: enums.Placement;
  timePicker?: boolean;
  defaultValue?: DateISO8601 | string;
  disabled?: boolean;
  defaultTime?: TimeHHMM;
  size?: enums.Size;
}

export const DatePicker = ({
  onChange,
  placeholder = ' date',
  placement,
  timePicker = false,
  defaultValue,
  disabled = false,
  defaultTime = '00:00',
  size = enums.Size.MEDIUM,
}: Props) => {
  const defaultDate: Date | undefined = defaultValue
    ? parseISO(defaultValue)
    : undefined;

  const [selected, setSelected] = useState<Date | undefined>(defaultDate);
  const { isOpen, anchor, togglePopover, closePopover } = usePopover();

  function handleChange(date: Date | undefined) {
    if (!date) {
      setSelected(undefined);
      onChange(undefined);
      return;
    }

    if (selected && timePicker) {
      const newDate = new Date(date);
      newDate.setHours(selected.getHours());
      newDate.setMinutes(selected.getMinutes());
      setSelected(newDate);
      onChange(newDate.toISOString());
    } else {
      const newDate = new Date(date);
      if (timePicker) {
        const [hours, minutes] = defaultTime
          .split(':')
          .map((str) => parseInt(str, 10));
        newDate.setHours(hours);
        newDate.setMinutes(minutes);
      }
      setSelected(newDate);
      onChange(newDate.toISOString());
    }
  }

  const handleTimeChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    const time = e.target.value;

    // Early return if time is not in valid HH:mm format
    if (!/^\d{2}:\d{2}$/.test(time)) {
      return;
    }

    const [hours, minutes] = time.split(':').map((str) => parseInt(str, 10));

    // If no date is selected, use today's date
    const dateToUse = selected || new Date();
    const newSelectedDate = setHours(setMinutes(dateToUse, minutes), hours);

    setSelected(newSelectedDate);
    onChange(newSelectedDate.toISOString());
  };

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        const today = document.querySelector(
          '.rdp-today > .rdp-day_button'
        ) as HTMLButtonElement;
        if (today) {
          today.focus();
        }
      }, 0);
    }
  }, [isOpen]);

  function reset() {
    setSelected(undefined);
    onChange(undefined);
  }

  const displayDateFormat = timePicker ? 'E dd MMM, h:mm a' : 'E dd MMM';

  const now = new Date();

  const beforeToday = selected && isBefore(selected, now);
  const today = selected && isToday(selected);

  const displayDate = today
    ? timePicker
      ? `Today, ${format(selected, 'h:mm a')}`
      : 'Today'
    : selected && format(selected, displayDateFormat);

  const timeValue = selected ? format(selected, 'HH:mm') : defaultTime;

  return (
    <ClickAwayListener onClickAway={closePopover}>
      <div className={s.datePicker}>
        <Button
          onClick={togglePopover}
          variant={enums.Variant.OUTLINE}
          disabled={disabled}
          size={size}
        >
          <span
            className={cx(u.flex, u.alignCenter, {
              [u.red4]: beforeToday,
              [u.green4]: today,
            })}
          >
            <Icon name={enums.Icons.CALENDAR} className={u.mr2} />
            {selected ? displayDate : placeholder}
          </span>
        </Button>
        <Popover
          isOpen={isOpen}
          anchor={anchor}
          placement={placement}
          closePopover={closePopover}
        >
          <div className={u.p3}>
            <DayPicker
              mode="single"
              selected={selected}
              onSelect={(e: Date | undefined) => handleChange(e)}
              components={{
                Chevron: ({ orientation }: ChevronProps) => (
                  <Icon
                    name={
                      orientation === 'right'
                        ? enums.Icons.CHEVRON_RIGHT
                        : enums.Icons.CHEVRON_LEFT
                    }
                  />
                ),
              }}
              footer={
                <>
                  {timePicker && (
                    <span>
                      <label htmlFor="time">Set time:</label>
                      <input
                        id="time"
                        type="time"
                        value={timeValue}
                        onChange={handleTimeChange}
                      />
                    </span>
                  )}
                  <div className={s.buttons}>
                    <Button
                      size={enums.Size.SMALL}
                      variant={enums.Variant.GHOST}
                      onClick={() => reset()}
                    >
                      Clear date
                    </Button>
                    {timePicker && (
                      <Button
                        variant={enums.Variant.FADE}
                        size={enums.Size.SMALL}
                        onClick={closePopover}
                      >
                        Done
                      </Button>
                    )}
                  </div>
                </>
              }
            />
          </div>
        </Popover>
      </div>
    </ClickAwayListener>
  );
};
